// A deliberately tiny Markdown renderer for the brand pack's legal documents
// (FR-73): "#"–"###" headings, paragraphs, "- " bullet lists and **bold**.
// It builds React elements from plain strings, so any HTML in the source is
// shown as text and never interpreted.
import { List, Stack, Text, Title } from "@mantine/core";
import type { ReactNode } from "react";

export type Block =
  | { kind: "heading"; level: 1 | 2 | 3; text: string }
  | { kind: "paragraph"; text: string }
  | { kind: "list"; items: string[] };

const HEADING = /^(#{1,3})\s+(.*)$/;
const BULLET = /^[-*]\s+(.*)$/;

/** parseMarkdown splits the source into blocks; lines inside a paragraph are joined with a space. */
export function parseMarkdown(source: string): Block[] {
  const blocks: Block[] = [];
  let para: string[] = [];
  let list: string[] | null = null;
  const flush = () => {
    if (para.length) blocks.push({ kind: "paragraph", text: para.join(" ") });
    if (list) blocks.push({ kind: "list", items: list });
    para = [];
    list = null;
  };
  for (const raw of source.replace(/\r\n?/g, "\n").split("\n")) {
    const line = raw.trim();
    if (!line) {
      flush();
      continue;
    }
    const h = HEADING.exec(line);
    if (h) {
      flush();
      blocks.push({ kind: "heading", level: (h[1]?.length ?? 1) as 1 | 2 | 3, text: h[2] ?? "" });
      continue;
    }
    const b = BULLET.exec(line);
    if (b) {
      if (para.length) flush();
      list = list ?? [];
      list.push(b[1] ?? "");
      continue;
    }
    if (list) {
      // A continuation line of the last bullet.
      const last = list.length - 1;
      list[last] = `${list[last] ?? ""} ${line}`;
      continue;
    }
    para.push(line);
  }
  flush();
  return blocks;
}

/** inline renders **bold** spans; everything else stays plain text. */
function inline(text: string): ReactNode[] {
  return text.split(/\*\*(.+?)\*\*/g).map((part, i) => (i % 2 === 1 ? <strong key={i}>{part}</strong> : part));
}

/** Markdown renders a legal document; its first-level heading is the page heading. */
export function Markdown({ source }: { source: string }) {
  const blocks = parseMarkdown(source);
  return (
    <Stack gap="sm" data-testid="markdown">
      {blocks.map((b, i) => {
        switch (b.kind) {
          case "heading":
            return (
              <Title
                key={i}
                order={b.level}
                size={b.level === 1 ? "h2" : b.level === 2 ? "h4" : "h5"}
                mt={i ? "sm" : 0}
              >
                {inline(b.text)}
              </Title>
            );
          case "list":
            return (
              <List key={i} spacing={4}>
                {b.items.map((item, j) => (
                  <List.Item key={j}>{inline(item)}</List.Item>
                ))}
              </List>
            );
          default:
            return <Text key={i}>{inline(b.text)}</Text>;
        }
      })}
    </Stack>
  );
}
